import {
    Autocomplete,
    Box,
    CircularProgress,
    createFilterOptions,
    SxProps,
    TextField,
    Theme,
} from '@mui/material'
import { Dispatch, Fragment, SetStateAction, useEffect, useState } from 'react'
import API from '@enums/api'
import { enqueueSnackbar, SnackbarProvider } from 'notistack'

export interface OrgDropDownOption {
    label: string
    id: string
}

type Props = {
    selectedOrg: string | undefined
    setSelectedOrg: Dispatch<SetStateAction<string | undefined>>
    setShowForm?: Dispatch<SetStateAction<boolean>>
    options?: OrgDropDownOption[]
    setOptions?: React.Dispatch<React.SetStateAction<OrgDropDownOption[]>>
    sx?: SxProps<Theme>
    required?: boolean
    disabled?: boolean
}

const SelectOrgs = (props: Props) => {
    const {
        setShowForm,
        setSelectedOrg,
        selectedOrg,
        setOptions,
        sx,
        required,
        disabled,
    } = props

    const [error, setError] = useState<string | null>(null)

    const [localOpts, setLocalOptions] = useState<readonly OrgDropDownOption[]>(
        []
    )
    const [open, setOpen] = useState(false)
    const loading = open && localOpts.length === 0

    const orgFetch = async () => {
        const res = await API.searches.listAllOrgs()

        if (!res.ok) {
            console.error(res.error)
            setError('Failed to fetch orgs')
            return []
        }

        return res.value
    }

    useEffect(() => {
        let active = true

        if (!loading) {
            return undefined
        }

        ;(async () => {
            const opts = await orgFetch()
            if (active) {
                if (setOptions) {
                    setOptions(opts)
                } else {
                    setLocalOptions(opts)
                }
            }
        })()

        return () => {
            active = false
        }
    }, [loading])

    if (error) {
        enqueueSnackbar(error, {
            variant: 'error',
        })
    }

    return (
        <Box
            style={{
                display: 'flex',
                justifyContent: 'center',
            }}
            sx={sx}
        >
            <Autocomplete
                disabled={disabled}
                style={{ width: '100%' }}
                disablePortal
                loading={loading}
                open={open}
                onOpen={() => {
                    setOpen(true)
                }}
                onClose={() => {
                    setOpen(false)
                }}
                isOptionEqualToValue={(op, va) => op.label === va.label}
                getOptionLabel={(op) => op.label}
                filterOptions={createFilterOptions({
                    matchFrom: 'start',
                    stringify: (option) => option.label,
                })}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        required={required}
                        label="Organisation"
                        InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                                <Fragment>
                                    {loading ? (
                                        <CircularProgress
                                            color="primary"
                                            size={20}
                                        />
                                    ) : null}
                                    {params.InputProps.endAdornment}
                                </Fragment>
                            ),
                        }}
                    />
                )}
                options={localOpts}
                defaultValue={localOpts.find((v) => v.id === selectedOrg)}
                onChange={(_e, value) => {
                    if (setShowForm) setShowForm(true)
                    setSelectedOrg(value ? value.id : '')
                }}
            />
            <SnackbarProvider
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            />
        </Box>
    )
}

export default SelectOrgs
